import { FC } from 'react';
import { Upload, UploadProps } from 'antd';
import cn from 'classnames';
import styled from 'styled-components';

import { COLORS } from '@/shared/constants/colors';
import { Button } from '@/shared/uikit/Button';
import { Icon, IconName } from '@/shared/uikit/Icon';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { numbers } from '@/shared/utils/numbers';

import { FileTypeIcon } from '../components/FileTypeIcon';

type UploadFileProps = {
  title: string;
  description: string;
  hideDropArea?: boolean;
  forceFileName?: string;
  icon?: IconName;
} & UploadProps;

export const UploadFile: FC<UploadFileProps> = ({
  description,
  title,
  hideDropArea,
  className,
  forceFileName,
  icon,
  ...props
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'signedWillInfo' });
  return (
    <DraggerStyled
      itemRender={(_node, file, _fileList, actions) => {
        const isError = file.status === 'error';
        const fileName = forceFileName || file.name;
        return (
          <Item>
            <Preview className={cn({ error: isError })}>
              {isError ? (
                <Icon name="arrow-clockwise" size="s20" />
              ) : file.percent !== 100 ? (
                <Icon name="spinner" size="s20" />
              ) : (
                <FileTypeIcon size="s20" ext={fileName?.split('.').pop() || ''} />
              )}
            </Preview>
            <ItemContent>
              <b>{forceFileName ?? file.name}</b>
              {isError ? (
                <Error>{t('error')}</Error>
              ) : (
                <span>
                  {file.percent !== 100 || !file.size
                    ? t('uploading', { num: file.percent?.toFixed(0) })
                    : numbers.formatMemoryFromBytes(file.size)}
                </span>
              )}
            </ItemContent>
            <ButtonStyled onClick={actions.remove} type="text" icon={<Icon name="trash" />} />
          </Item>
        );
      }}
      className={cn(className, { hideDropArea: hideDropArea })}
      {...props}
    >
      <Icon name={icon ?? 'plus'} size="s20" />
      <b>{title}</b>
      <span>{description}</span>
    </DraggerStyled>
  );
};

const DraggerStyled = styled(Upload.Dragger)`
  &&& {
    &.hideDropArea {
      min-height: 54px;
    }

    .ant-upload {
      background: unset;
      border-width: 2px;
    }

    &.hideDropArea .ant-upload {
      display: none;
    }

    .ant-upload-drag {
      height: unset;
    }

    .ant-upload-list {
      margin-top: 8px;
    }

    .ant-upload-drag-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100px;
      gap: 4px;
    }
  }
`;

const Preview = styled.div`
  background: ${COLORS.systemSky100};
  height: 36px;
  width: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  flex-shrink: 0;

  &.error {
    background: ${COLORS.systemFlame100};
    color: ${COLORS.systemFlame200};
  }
`;

const Item = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  min-height: 54px;
`;

const ItemContent = styled.div`
  flex: 2;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ButtonStyled = styled(Button)`
  color: ${COLORS.systemDark200};
`;

const Error = styled.span`
  color: ${COLORS.systemFlame200};
`;
